import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles, AlertCircle, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";

/**
 * Landing target for the upstream provider's returnUrl after async payment
 * flows (PayPal / Klarna / PayID / card-with-3DS). The provider redirects the
 * customer to `/pay/return` with query params containing the session id.
 *
 * Tolerant to many param name spellings. If the upstream sends us back
 * WITHOUT a session id (customer hit cancel, 3DS failed, browser back button,
 * abandoned mid-flow), DO NOT bounce them to the homepage — that erases all
 * context. Show a clear "Payment incomplete" panel instead.
 */
const SESSION_PARAM_KEYS = [
  "id", "sessionId", "session_id", "orderId", "order_id", "ref", "transactionId",
];

export default function CheckoutReturn() {
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState("resolving"); // 'resolving' | 'forwarded' | 'lost'

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let sid = null;
    for (const k of SESSION_PARAM_KEYS) {
      const v = params.get(k);
      if (v && /^ps_/.test(v)) { sid = v; break; }
    }
    if (!sid) {
      for (const [, v] of params) {
        if (typeof v === "string" && /^ps_[\w-]{8,}/.test(v)) { sid = v; break; }
      }
    }
    // Fallback: CheckoutFrame stashes the session id in localStorage when the
    // customer first loads the checkout page. Some providers (Banxa included)
    // strip ALL query params on cancel/return — without this fallback we'd
    // bounce the customer to "Payment not completed" even when we have the
    // session id stashed locally and could resume the polling loop.
    if (!sid) {
      try {
        const stashed = localStorage.getItem("xch_last_session_id");
        if (stashed && /^ps_[\w-]{8,}/.test(stashed)) sid = stashed;
      } catch (_e) { /* private mode / quota */ }
    }
    const t = setTimeout(() => {
      if (sid) {
        setOutcome("forwarded");
        navigate(`/pay/checkout/${sid}`, { replace: true });
      } else {
        setOutcome("lost");
      }
    }, 250);
    return () => clearTimeout(t);
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="checkout-return">
      <header className="bg-foreground text-white">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-emerald-300" />
          </span>
          <div className="font-display font-bold text-base">Xchangely <span className="text-emerald-300">Pay</span></div>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-5">
        {outcome === "resolving" && (
          <div className="text-center" data-testid="checkout-return-resolving">
            <Loader2 className="w-7 h-7 animate-spin text-emerald-700 mx-auto mb-4" />
            <h1 className="font-display font-bold text-xl mb-1">Finishing up your payment…</h1>
            <p className="text-sm text-muted-foreground">Confirming with the merchant — one moment.</p>
          </div>
        )}
        {outcome === "lost" && (
          <div className="max-w-md w-full xch-card p-8 text-center" data-testid="checkout-return-lost">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center bg-amber-50 border border-amber-200">
              <AlertCircle className="w-7 h-7 text-amber-700" />
            </div>
            <h1 className="font-display font-bold text-2xl mb-2">Payment not completed</h1>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              It looks like you cancelled or stepped away before the payment was
              authorised. <strong>Nothing has been charged.</strong> Ask the merchant
              for a new payment link if you'd like to try again.
            </p>
            <Button asChild variant="outline" className="rounded-xl gap-1.5" data-testid="checkout-return-home-btn">
              <a href="/">Back to Xchangely.io <ArrowRight className="w-3.5 h-3.5" /></a>
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
